// src/pages/ForecastsPage.js

import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Tabs, Tab, Paper } from '@mui/material';
import MainGrid from '../../components/Dashboard/MainGrid';
import ChartCard from '../../components/Dashboard/ChartCard';
import ForecastChart from '../../components/Dashboard/ForecastChart';
import EFIchart from '../../components/Dashboard/EFIchart';
import EFIpieChart from '../../components/Dashboard/EFIpieChart';
import IconCard from '../../components/Dashboard/IconCard';
import SuggestionsCard from '../../components/Dashboard/SuggestionsCard';
import {
  forecastsTooltips,
  categoryColors,
  flexibilityCategories
} from '../../config/dashboardConfig';

const ForecastsPage = ({ forecastsAPIData, selectedDashboardOption, flexibilityDays }) => {
  // State variables for API data
  const [dailySummaries, setDailySummaries] = useState([]);

  // State for Tabs
  const [selectedDay, setSelectedDay] = useState(1); // Default to Day 1

  useEffect(() => {
    if (forecastsAPIData && forecastsAPIData.dailySummaries) {
      setDailySummaries(forecastsAPIData.dailySummaries);
    } else {
      setDailySummaries([]); // Reset if no data
    }
  }, [forecastsAPIData]); // Update local dailySummaries when prop changes

  // If forecastsAPIData is not yet loaded (handled by parent, but good for safety)
  if (!forecastsAPIData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 3 }}>
        <Typography>Waiting for forecast data...</Typography>
      </Box>
    );
  }

  // Check if we have any forecast data available
  const hasAnyForecastData = forecastsAPIData.hasDailySummaries ||
    forecastsAPIData.hasNextDayForecast ||
    forecastsAPIData.hasNextDaysForecast ||
    forecastsAPIData.hasEfiData ||
    forecastsAPIData.hasHoursData;

  if (!hasAnyForecastData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', p: 3 }}>
        <Typography>No forecast data available. Some API endpoints may be experiencing issues.</Typography>
      </Box>
    );
  }

  // Placeholder values for other props (update as needed)
  const modelName = 'M1';
  const country = 'greece';
  const method = selectedDashboardOption;

  const handleTabChange = (event, newValue) => {
    setSelectedDay(newValue);
  };

  // Get the summary for the selected day
  const selectedDaySummary = dailySummaries[selectedDay - 1]; // Assuming dailySummaries is 0-indexed

  return (
    <Box sx={{ mt: 2, mx: 2 }}>
      <MainGrid>
        {/* Forecast Charts Section */}
        {/* Next Day Forecast - Consumption */}
        {forecastsAPIData.hasNextDayForecast && (
          <Grid item xs={12} md={6}>
            <ChartCard
              title="Next Day Forecast - Consumption"
              tooltip={forecastsTooltips.nextDayForecast}
            >
              <ForecastChart
                data={forecastsAPIData.nextDayConsumption}
                days={1}
                type="consumption"
              />
            </ChartCard>
          </Grid>
        )}

        {/* Next Day Forecast - Production */}
        {forecastsAPIData.hasNextDayForecast && (
          <Grid item xs={12} md={6}>
            <ChartCard
              title="Next Day Forecast - Production"
              tooltip={forecastsTooltips.nextDayForecastProduction}
            >
              <ForecastChart
                data={forecastsAPIData.nextDayProduction}
                days={1}
                type="production"
              />
            </ChartCard>
          </Grid>
        )}

        {/* Next X-Days Forecast - Consumption */}
        {forecastsAPIData.hasNextDaysForecast && (
          <Grid item xs={12} md={6}>
            <ChartCard
              title={`Next ${flexibilityDays}-Days Forecast - Consumption`}
              tooltip={forecastsTooltips.nextDaysForecast}
            >
              <ForecastChart
                data={forecastsAPIData.nextDaysConsumption}
                days={flexibilityDays}
                type="consumption"
              />
            </ChartCard>
          </Grid>
        )}

        {/* Next 3-Days Forecast - Production */}
        {forecastsAPIData.hasNextDaysForecast && (
          <Grid item xs={12} md={6}>
            <ChartCard
              title={`Next ${flexibilityDays}-Days Forecast - Production`}
              tooltip={forecastsTooltips.nextDaysForecastProduction}
            >
              <ForecastChart
                data={forecastsAPIData.nextDaysProduction}
                days={flexibilityDays}
                type="production"
              />
            </ChartCard>
          </Grid>
        )}

        {/* Flexibility Analysis Section with Light Grey Background */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
              borderRadius: 2,
              p: 3,
              mt: 2,
            }}
          >
            <Typography
              variant="h5"
              component="h2"
              sx={{
                mb: 3,
                fontWeight: 600,
                color: 'text.primary',
                textAlign: 'center'
              }}
            >
              Flexibility Analysis
            </Typography>

            <MainGrid>
              {/* Flexibility Categories Explanation */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {flexibilityCategories.map((category, index) => (
                    <Grid item xs={12} md={3} key={index}>
                      <IconCard
                        title={category.title}
                        description={category.description}
                        icon={category.icon}
                        color={category.color}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              {/* EFI Chart */}
              {(forecastsAPIData.hasEfiData || forecastsAPIData.hasHoursData) && (
                <Grid item xs={12}>
                  <ChartCard
                    title="EFI UP and EFI DOWN per Hour"
                    tooltip={forecastsTooltips.efiChart}
                  >
                    <EFIchart
                      method={method}
                      efiData={forecastsAPIData.efiData}
                      days={flexibilityDays}
                    />
                  </ChartCard>
                </Grid>
              )}

              {/* Tabbed Box for Donut Chart and Suggestions */}
              {(forecastsAPIData.hasEfiData || forecastsAPIData.hasDailySummaries) && (
                <Grid item xs={12}>
                  <ChartCard
                    title="Flexibility Categories and Daily Suggestions"
                  // tooltip={forecastsTooltips.efiPie}
                  >
                    <Box sx={{ width: '100%' }}>
                      {/* Tabs */}
                      <Tabs
                        value={selectedDay}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        aria-label="Day Tabs"
                        sx={{ borderBottom: 1, borderColor: 'divider' }}
                      >
                        {[...Array(flexibilityDays)].map((_, index) => (
                          <Tab key={index} label={`Day ${index + 1}`} value={index + 1} />
                        ))}
                      </Tabs>

                      {/* Tab Panels */}
                      <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
                        {/* Donut Chart inside ChartCard */}
                        {forecastsAPIData.hasEfiData && (
                          <Box sx={{ flex: '0 0 40%' }}>
                            <ChartCard
                              title="Flexibility Categories Distribution"
                              subtitle="Consumption & Production"
                              tooltip="Percentage of each flexibility category during the day."
                            >
                              <EFIpieChart
                                day={selectedDay}
                                method={method}
                                efiData={forecastsAPIData.efiData}
                              />
                            </ChartCard>
                          </Box>
                        )}

                        {/* Suggestions */}
                        <Box sx={{ flex: forecastsAPIData.hasEfiData ? '1 1 60%' : '1 1 100%' }}>
                          {forecastsAPIData.hasDailySummaries && selectedDaySummary ? (
                            <SuggestionsCard daySummary={selectedDaySummary} />
                          ) : (
                            <Typography>No suggestions data available for this day.</Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </ChartCard>
                </Grid>
              )}
            </MainGrid>
          </Paper>
        </Grid>
      </MainGrid>
    </Box>
  );
};

export default ForecastsPage;
